// =============================================================================
// PORTFOLIO CONTENT DATA
// =============================================================================

import type { ContentComponent } from '@/types';

// Individual project data structure
export interface ProjectData {
  slug: string;
  title: string;
  subtitle: string;
  description: string;
  longDescription: string;
  image: string;
  url: string;
  technologies: string[];
  status: string;
  launchDate: string;
  features: string[];
  metrics?: {
    growth?: string;
    users?: string;
    performance?: string;
  };
  testimonial?: {
    quote: string;
    author: string;
    role?: string;
  };
  seo: {
    title: string;
    description: string;
    keywords: string;
    canonicalUrl: string;
  };
}

// Portfolio page content components
export const portfolioPageComponents: ContentComponent[] = [
  {
    id: 'hero-portfolio',
    type: 'hero',
    title: 'A Living Portfolio of Evolving Products',
    content: 'Unlike static case studies, my portfolio is alive. These are active client projects that demonstrate the power of iterative development, showcasing real-time growth, continuous improvement, and lasting partnerships.',
    fullWidth: true,
    metadata: {
      image: '/images/showcase.jpg',
      overlayOpacity: 0.8,
      minHeight: '80vh',
      buttons: [
        {
          label: 'View Live Projects',
          variant: 'primary',
          size: 'lg',
          action: 'scroll',
          target: 'projects-section'
        },
        {
          label: 'Start Your Project',
          variant: 'secondary',
          size: 'lg',
          action: 'internal',
          url: '/contact'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'portfolio-intro',
    type: 'text',
    title: 'Why a Live Portfolio?',
    content: `
      <p>A traditional portfolio is a snapshot in time. A live portfolio is a testament to a better development philosophy. It demonstrates that a product is never truly "done"; it's a living entity that should adapt and grow with your business and your users.</p>
      
      <h3>The Iterative Advantage in Action:</h3>
      <ul>
        <li><strong>Proof of Partnership:</strong> My client relationships are ongoing, focused on long-term success, not just a one-time launch.</li>
        <li><strong>Transparent Process:</strong> See my methodology at work. These projects evolve week by week based on real data and strategic goals.</li>
        <li><strong>Real-World Results:</strong> Watch as features are added, user experiences are refined, and performance is optimized in real time.</li>
        <li><strong>Commitment to Growth:</strong> This is my core belief in action; that the true value of a digital product lies in its ability to continuously deliver results long after its initial release.</li>
      </ul>
      
      <p>This is the data-first, iterative approach made visible. Every project here is a story of continuous value creation.</p>
    `,
    metadata: {
      textAlign: 'left',
      maxWidth: 800
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'projects-section',
    type: 'card',
    title: 'Featured Live Projects',
    content: 'Explore our active client projects and see iterative development in action. Each site represents an ongoing partnership focused on continuous improvement and growth.',
    metadata: {
      columns: 3,
      cards: [
        {
          title: 'The Yoga Encyclopedia',
          subtitle: 'Comprehensive Health & Wellness Platform',
          content: 'A comprehensive digital platform dedicated to yoga, wellness, and holistic health. Features extensive content management, user engagement tools, and community features. Built with modern web technologies and continuously evolving based on user needs and wellness trends.',
          image: '/images/yoga-encyclopedia.jpg',
          buttons: [
            {
              label: 'Visit Live Site',
              variant: 'primary',
              action: 'external',
              url: 'https://theyogaencyclopedia.com'
            },
            {
              label: 'View Project Details',
              variant: 'secondary',
              action: 'internal',
              url: '/portfolio/yoga-encyclopedia'
            }
          ],
          metadata: {
            technologies: ['React', 'TypeScript', 'Node.js', 'Firebase', 'SCSS'],
            status: 'Active Development',
            launchDate: '2025',
            features: [
              'Comprehensive content management system',
              'User authentication and profiles',
              'Interactive wellness tracking',
              'Community features and forums',
              'Mobile-responsive design',
              'SEO optimization'
            ]
          }
        },
        {
          title: 'Novel Novela',
          subtitle: 'Interactive Choose Your Own Adventure Platform',
          content: 'A modern take on interactive fiction, bringing choose-your-own-adventure stories to the digital age. Features dynamic storytelling, user choice tracking, and immersive reading experiences. Continuously expanding with new stories and interactive features.',
          image: '/images/novel-novela.jpg',
          buttons: [
            {
              label: 'Visit Live Site',
              variant: 'primary',
              action: 'external',
              url: 'https://novelnovela.com'
            },
            {
              label: 'View Project Details',
              variant: 'secondary',
              action: 'internal',
              url: '/portfolio/novel-novela'
            }
          ],
          metadata: {
            technologies: ['React', 'TypeScript', 'Node.js', 'MongoDB', 'CSS Modules'],
            status: 'Active Development',
            launchDate: '2025',
            features: [
              'Interactive story engine',
              'Choice tracking and analytics',
              'User progress saving',
              'Multiple story formats',
              'Social sharing features',
              'Mobile-optimized reading'
            ]
          }
        },
        {
          title: 'Voltage and Vibes',
          subtitle: 'Biofield Tuning & Harmonic Therapeutic Services',
          content: 'A beautiful wellness platform dedicated to Biofield Tuning and sound therapy. Features online booking, service information, and educational resources about harmonic healing. Designed to create a serene digital experience that mirrors the calming nature of the therapeutic services offered.',
          image: '/images/voltage-and-vibes.jpg',
          buttons: [
            {
              label: 'Visit Live Site',
              variant: 'primary',
              action: 'external',
              url: 'https://voltageandvibes.com'
            },
            {
              label: 'View Project Details',
              variant: 'secondary',
              action: 'internal',
              url: '/portfolio/voltage-and-vibes'
            }
          ],
          metadata: {
            technologies: ['React', 'TypeScript', 'Node.js', 'Firebase', 'SCSS'],
            status: 'Active Development',
            launchDate: '2025',
            features: [
              'Online booking system',
              'Service catalog and descriptions',
              'Educational content about Biofield Tuning',
              'Testimonials and success stories',
              'Mobile-responsive design',
              'SEO optimization for wellness services'
            ]
          }
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'development-process',
    type: 'features',
    title: 'My Iterative Development Process',
    content: 'A transparent, data-driven methodology for transforming ideas into continuously evolving digital products.',
    metadata: {
      columns: 3,
      features: [
        {
          title: 'Week 1: Foundation & MVP',
          description: 'Rapid development of a Minimum Viable Product with core functionality. Your idea goes from concept to live in under 7 days.',
          icon: 'fi-ts-brand-strategy'
        },
        {
          title: 'Ongoing: Data-Driven Evolution',
          description: 'Weekly development cycles add features, improvements, and optimizations based on real user feedback and analytics.',
          icon: 'fi-ts-optimization'
        },
        {
          title: 'Continuous: Strategic Growth',
          description: 'A long-term partnership ensures your product grows intelligently with your business and the market, maximizing its impact.',
          icon: ' fi-ts-steps-carreer'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'portfolio-stats',
    type: 'stats',
    title: 'Portfolio Performance',
    content: 'Real metrics from our live client projects, demonstrating the ongoing value of iterative development.',
    metadata: {
      columns: 4,
      stats: [
        {
          number: '100%',
          label: 'Active Projects',
          description: 'All projects continuously evolving'
        },
        {
          number: '52+',
          label: 'Weekly Updates',
          description: 'Per project per year'
        },
        {
          number: '300%',
          label: 'Average Growth',
          description: 'In user engagement'
        },
        {
          number: '24/7',
          label: 'Monitoring',
          description: 'Performance tracking'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'development-showcase',
    type: 'image-showcase',
    title: 'Continuous Development in Action',
    content: 'Our iterative approach means your project is never "finished" - it\'s continuously evolving, improving, and growing with your business needs.',
    metadata: {
      backgroundImage: '/images/cowork2.jpg',
      overlayOpacity: 0.7,
      buttons: [
        {
          label: 'See Our Process',
          variant: 'primary',
          size: 'lg',
          action: 'internal',
          url: '/services'
        },
        {
          label: 'Start Your Project',
          variant: 'secondary',
          size: 'lg',
          action: 'internal',
          url: '/contact'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'client-testimonials',
    type: 'text',
    title: 'Client Success Stories',
    content: `
      <blockquote>
        <p>"Working with Alex has transformed our digital presence. The iterative approach means our site is always improving, always adding value for our users. It's like having a development team that never stops working on your success."</p>
        <cite>- The Yoga Encyclopedia Team</cite>
      </blockquote>
      
      <blockquote>
        <p>"The continuous development cycle has been game-changing. Instead of launching and forgetting, our platform grows with our audience. New features appear regularly, and our users love the constant evolution."</p>
        <cite>- Novel Novela Founder</cite>
      </blockquote>
      
      <p>These testimonials reflect the real value of iterative development: ongoing improvement, continuous value delivery, and long-term partnership focused on your success.</p>
    `,
    metadata: {
      textAlign: 'left',
      maxWidth: 800
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'portfolio-cta',
    type: 'hero',
    title: 'Ready to Join Our Live Portfolio?',
    content: 'Let\'s discuss your project and show you how iterative development can transform your digital presence into a continuously evolving success story.',
    fullWidth: true,
    metadata: {
      image: '/images/requirements-gathering.jpg',
      overlayOpacity: 0.8,
      minHeight: '60vh',
      buttons: [
        {
          label: 'Start Your Project',
          variant: 'primary',
          size: 'lg',
          action: 'internal',
          url: '/contact'
        },
        {
          label: 'Learn More About Our Process',
          variant: 'secondary',
          size: 'lg',
          action: 'internal',
          url: '/services'
        }
      ]
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  }
];

// Individual project data
export const projectsData: ProjectData[] = [
  {
    slug: 'yoga-encyclopedia',
    title: 'The Yoga Encyclopedia',
    subtitle: 'Comprehensive Health & Wellness Platform',
    description: 'A comprehensive digital platform dedicated to yoga, wellness, and holistic health. Features extensive content management, user engagement tools, and community features.',
    longDescription: `
      <p>The Yoga Encyclopedia represents a comprehensive digital transformation of traditional wellness knowledge into an interactive, accessible platform. Built with modern web technologies, this project showcases the power of iterative development in creating meaningful digital experiences.</p>
      
      <h3>Project Overview</h3>
      <p>This platform serves as a complete resource for yoga practitioners, wellness enthusiasts, and health professionals. The site combines educational content, interactive tools, and community features to create a holistic wellness experience.</p>
      
      <h3>Key Features</h3>
      <ul>
        <li><strong>Comprehensive Content Management:</strong> Extensive library of yoga poses, breathing techniques, and wellness articles</li>
        <li><strong>User Authentication & Profiles:</strong> Personalized experience with progress tracking and saved content</li>
        <li><strong>Interactive Wellness Tracking:</strong> Tools for monitoring practice, setting goals, and tracking progress</li>
        <li><strong>Community Features:</strong> Forums, user-generated content, and social sharing capabilities</li>
        <li><strong>Mobile-Responsive Design:</strong> Optimized for all devices with touch-friendly interactions</li>
        <li><strong>SEO Optimization:</strong> Comprehensive search engine optimization for health and wellness keywords</li>
      </ul>
      
      <h3>Technical Implementation</h3>
      <p>Built with React and TypeScript for type safety and maintainability, the platform uses Firebase for backend services including authentication, database, and hosting. The architecture supports real-time updates and scalable content management.</p>
      
      <h3>Continuous Development</h3>
      <p>This project exemplifies our iterative development approach. Regular updates include new content, feature enhancements, performance optimizations, and user experience improvements based on real user feedback and analytics.</p>
    `,
    image: '/images/yoga-encyclopedia.jpg',
    url: 'https://theyogaencyclopedia.com',
    technologies: ['React', 'JavaScript', 'Node.js', 'Firebase', 'CSS Modules'],
    status: 'Active Development',
    launchDate: '2025',
    features: [
      'Comprehensive content management system',
      'User authentication and profiles',
      'Interactive wellness tracking',
      'Community features and forums',
      'Mobile-responsive design',
      'SEO optimization'
    ],
    metrics: {
      growth: '300% increase in user engagement',
      users: 'Growing community of wellness enthusiasts',
      performance: 'Sub-2s load times across all devices'
    },
    testimonial: {
      quote: "Working with Alex has transformed our digital presence. The iterative approach means our site is always improving, always adding value for our users. It's like having a development team that never stops working on your success.",
      author: "The Yoga Encyclopedia Team",
      role: "Client"
    },
    seo: {
      title: 'The Yoga Encyclopedia - Live Project | Alex Finger Consulting',
      description: 'Explore The Yoga Encyclopedia, a comprehensive health and wellness platform built with React and TypeScript. See iterative development in action with continuous improvements and real growth metrics.',
      keywords: 'yoga encyclopedia, wellness platform, React development, TypeScript, health website, iterative development, live project, Alex Finger Consulting',
      canonicalUrl: '/portfolio/yoga-encyclopedia'
    }
  },
  {
    slug: 'novel-novela',
    title: 'Novel Novela',
    subtitle: 'Interactive Choose Your Own Adventure Platform',
    description: 'A modern take on interactive fiction, bringing choose-your-own-adventure stories to the digital age with dynamic storytelling and immersive reading experiences.',
    longDescription: `
      <p>Novel Novela reimagines interactive fiction for the digital age, combining traditional storytelling with modern web technologies to create engaging, immersive reading experiences.</p>
      
      <h3>Project Overview</h3>
      <p>This platform brings the classic "choose your own adventure" format into the modern era with enhanced interactivity, social features, and analytics. Users can create, share, and experience interactive stories in a beautifully designed interface.</p>
      
      <h3>Key Features</h3>
      <ul>
        <li><strong>Interactive Story Engine:</strong> Dynamic story progression based on user choices with branching narratives</li>
        <li><strong>Choice Tracking & Analytics:</strong> Detailed analytics on user choices and story paths for content creators</li>
        <li><strong>User Progress Saving:</strong> Save and resume reading sessions across devices</li>
        <li><strong>Multiple Story Formats:</strong> Support for various interactive fiction formats and styles</li>
        <li><strong>Social Sharing Features:</strong> Share favorite stories and choices with the community</li>
        <li><strong>Mobile-Optimized Reading:</strong> Seamless reading experience across all devices</li>
      </ul>
      
      <h3>Technical Implementation</h3>
      <p>Built with React and TypeScript, the platform uses MongoDB for flexible story data storage and Node.js for the backend API. The architecture supports complex branching narratives and real-time user interactions.</p>
      
      <h3>Continuous Development</h3>
      <p>Regular updates include new story features, improved user interface elements, enhanced analytics, and performance optimizations. The platform evolves based on user feedback and usage patterns.</p>
    `,
    image: '/images/novel-novela.jpg',
    url: 'https://novelnovela.com',
    technologies: ['React', 'TypeScript', 'Node.js', 'Firebase', 'SCSS'],
    status: 'Active Development',
    launchDate: '2025',
    features: [
      'Interactive story engine',
      'Choice tracking and analytics',
      'User progress saving',
      'Multiple story formats',
      'Social sharing features',
      'Mobile-optimized reading'
    ],
    metrics: {
      growth: '250% increase in story completions',
      users: 'Growing community of interactive fiction enthusiasts',
      performance: 'Instant story loading and smooth transitions'
    },
    testimonial: {
      quote: "The continuous development cycle has been game-changing. Instead of launching and forgetting, our platform grows with our audience. New features appear regularly, and our users love the constant evolution.",
      author: "Novel Novela Founder",
      role: "Client"
    },
    seo: {
      title: 'Novel Novela - Interactive Fiction Platform | Alex Finger Consulting',
      description: 'Discover Novel Novela, an interactive choose-your-own-adventure platform built with React and TypeScript. Experience iterative development with continuous feature updates and user engagement improvements.',
      keywords: 'novel novela, interactive fiction, choose your own adventure, React development, TypeScript, storytelling platform, live project, Alex Finger Consulting',
      canonicalUrl: '/portfolio/novel-novela'
    }
  },
  {
    slug: 'voltage-and-vibes',
    title: 'Voltage and Vibes',
    subtitle: 'Biofield Tuning & Harmonic Therapeutic Services',
    description: 'A beautiful wellness platform dedicated to Biofield Tuning and sound therapy, helping clients find healing and harmony through harmonic therapeutic services.',
    longDescription: `
      <p>Voltage and Vibes brings the healing power of Biofield Tuning and sound therapy to a wider audience through a beautifully designed, accessible digital platform. This project showcases how thoughtful web design can reflect the calming, therapeutic nature of the services offered.</p>
      
      <h3>Project Overview</h3>
      <p>This wellness platform serves as the digital home for a Biofield Tuning practice, combining service information, online booking, and educational content about harmonic healing. The design emphasizes tranquility and ease of use, creating a digital experience that mirrors the peaceful nature of sound therapy.</p>
      
      <h3>Key Features</h3>
      <ul>
        <li><strong>Online Booking System:</strong> Easy-to-use scheduling for therapy sessions with calendar integration</li>
        <li><strong>Service Catalog:</strong> Comprehensive information about Biofield Tuning and various therapeutic offerings</li>
        <li><strong>Educational Resources:</strong> Articles and content explaining the science and benefits of sound therapy</li>
        <li><strong>Testimonials & Success Stories:</strong> Real client experiences showcasing the impact of Biofield Tuning</li>
        <li><strong>Mobile-Responsive Design:</strong> Seamless experience across all devices with calming, intuitive interface</li>
        <li><strong>SEO Optimization:</strong> Comprehensive optimization for wellness and alternative therapy keywords</li>
      </ul>
      
      <h3>Technical Implementation</h3>
      <p>Built with React and TypeScript for a smooth, modern user experience, the platform uses Firebase for backend services including booking management and content delivery. The design focuses on accessibility and creating a sense of calm through thoughtful color choices and spacing.</p>
      
      <h3>Continuous Development</h3>
      <p>Regular updates include new educational content, booking feature enhancements, performance optimizations, and user experience improvements based on client feedback and user behavior analytics.</p>
    `,
    image: '/images/voltage-and-vibes.jpg',
    url: 'https://voltageandvibes.com',
    technologies: ['React', 'TypeScript', 'Node.js', 'Firebase', 'SCSS'],
    status: 'Active Development',
    launchDate: '2025',
    features: [
      'Online booking system',
      'Service catalog and descriptions',
      'Educational content about Biofield Tuning',
      'Testimonials and success stories',
      'Mobile-responsive design',
      'SEO optimization for wellness services'
    ],
    metrics: {
      growth: '200% increase in online bookings',
      users: 'Growing community of wellness seekers',
      performance: 'Fast load times with peaceful, calming design'
    },
    testimonial: {
      quote: "The website perfectly captures the essence of our practice. It's not just a booking platform; it's an extension of the healing experience we provide. Alex understood our vision and created something that truly resonates with our clients.",
      author: "Voltage and Vibes Team",
      role: "Client"
    },
    seo: {
      title: 'Voltage and Vibes - Biofield Tuning Platform | Alex Finger Consulting',
      description: 'Explore Voltage and Vibes, a wellness platform dedicated to Biofield Tuning and sound therapy. Built with React and TypeScript, featuring online booking and educational resources about harmonic healing.',
      keywords: 'voltage and vibes, biofield tuning, sound therapy, wellness platform, harmonic healing, React development, TypeScript, wellness website, Alex Finger Consulting',
      canonicalUrl: '/portfolio/voltage-and-vibes'
    }
  }
];

// Helper function to get project by slug
export const getProjectBySlug = (slug: string): ProjectData | undefined => {
  return projectsData.find(project => project.slug === slug);
};

// Helper function to get all project slugs for static generation
export const getAllProjectSlugs = (): string[] => {
  return projectsData.map(project => project.slug);
};
